import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type AudioDictationStatus = 'idle' | 'recording' | 'uploading' | 'transcribing' | 'error';

interface UseAudioDictationOptions {
  onText: (text: string) => void;
  maxDurationMs?: number;
  maxBytes?: number;
}

interface UseAudioDictationReturn {
  status: AudioDictationStatus;
  error: string | null;
  isSupported: boolean;
  startRecording: () => void;
  stopRecording: () => void;
  cancel: () => void;
  elapsedSeconds: number;
}

function getPreferredMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/webm',
  ];
  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

function mimeToExt(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'mp4';
  return 'webm';
}

export function useAudioDictation(options: UseAudioDictationOptions): UseAudioDictationReturn {
  const { onText, maxDurationMs = 60_000, maxBytes = 10 * 1024 * 1024 } = options;

  const [status, setStatus] = useState<AudioDictationStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mimeTypeRef = useRef('');
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  const isSupported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    if (mediaRecorderRef.current?.state === 'recording') {
      // Stop without triggering onstop transcription
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    cleanup();
    setStatus('idle');
    setElapsedSeconds(0);
    setError(null);
  }, [cleanup]);

  const transcribe = useCallback(async (blob: Blob) => {
    setStatus('uploading');
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Not authenticated.');
        setStatus('error');
        return;
      }

      const formData = new FormData();
      const ext = mimeToExt(mimeTypeRef.current);
      formData.append('file', blob, `recording.${ext}`);
      formData.append('mimeType', mimeTypeRef.current);

      setStatus('transcribing');

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: formData,
        signal: controller.signal,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ error: 'Transcription failed' }));
        throw new Error(body.error || 'Transcription failed');
      }

      const data = await resp.json();
      if (data.text) {
        onTextRef.current(data.text);
      }
      setStatus('idle');
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setStatus('idle');
        return;
      }
      setError(e.message || 'Transcription failed');
      setStatus('error');
    } finally {
      abortRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (maxTimerRef.current) { clearTimeout(maxTimerRef.current); maxTimerRef.current = null; }
  }, []);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError("Dictation not available. Try typing.");
      setStatus('error');
      return;
    }

    setError(null);
    setElapsedSeconds(0);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = getPreferredMimeType();
      mimeTypeRef.current = mimeType;

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // Release mic
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        chunksRef.current = [];

        if (blob.size > maxBytes) {
          setError('Recording too large (max 10MB).');
          setStatus('error');
          return;
        }

        if (blob.size < 100) {
          // Too short / empty
          setStatus('idle');
          return;
        }

        transcribe(blob);
      };

      recorder.onerror = () => {
        setError('Recording failed.');
        setStatus('error');
        cleanup();
      };

      recorder.start(250); // collect chunks every 250ms
      setStatus('recording');

      // Elapsed timer
      timerRef.current = setInterval(() => {
        setElapsedSeconds(s => s + 1);
      }, 1000);

      // Max duration guard
      maxTimerRef.current = setTimeout(() => {
        stopRecording();
      }, maxDurationMs);

    } catch (e: any) {
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        setError('Microphone permission denied.');
      } else if (e.name === 'NotFoundError') {
        setError('No microphone found.');
      } else {
        setError('Could not start recording.');
      }
      setStatus('error');
      cleanup();
    }
  }, [isSupported, maxBytes, maxDurationMs, cleanup, stopRecording, transcribe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    status,
    error,
    isSupported,
    startRecording,
    stopRecording,
    cancel,
    elapsedSeconds,
  };
}
